/**
 * Serviço de Anos Letivos
 *
 * Gerencia operações CRUD de anos letivos usando PostgREST.
 */

import { postgrestClient } from "../lib/postgrest/client";

/**
 * Serviço de anos letivos
 */
export const anosLetivosService = {
  /**
   * Lista anos letivos com filtros
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { status: "eq.ativo" })
   * @param {string} [options.negocioId] - UUID do tenant (perfil /me); filtra anos letivos do tenant
   * @param {string} options.order - Ordenação (ex: "ano.desc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, negocioId, order = "ano.desc", limit, offset, includeCount = false } = options;

    // Filtros efetivos: incluir negocio_id quando multitenant
    const effectiveFilters = { ...filters };
    if (negocioId) {
      effectiveFilters.negocio_id = `eq.${negocioId}`;
    }

    // Se não há filtros específicos (apenas order, limit, offset), usar função RPC com estatísticas
    const hasFilters = Object.keys(effectiveFilters).length > 0;

    if (!hasFilters && !includeCount) {
      try {
        // Usar função RPC que inclui estatísticas calculadas (aceita p_negocio_id para tenant)
        const result = await postgrestClient.rpc("get_anos_letivos_com_estatisticas", {
          p_negocio_id: negocioId || null,
        });

        // A função retorna JSONB, então pode vir como objeto direto ou array
        let data = result;

        // Se result for um array com um objeto que tem get_anos_letivos_com_estatisticas
        if (Array.isArray(result) && result.length > 0 && result[0].hasOwnProperty('get_anos_letivos_com_estatisticas')) {
          data = result[0].get_anos_letivos_com_estatisticas;
        }

        const processedData = Array.isArray(data) ? data : data ? [data] : [];

        // Aplicar paginação se necessário
        let finalData = [...processedData];
        if (offset) {
          finalData = finalData.slice(offset);
        }
        if (limit) {
          finalData = finalData.slice(0, limit);
        }

        return finalData;
      } catch (rpcErr) {
        // Fallback se a RPC não existir (ex.: migration 144 não aplicada) ou 404
        const is404 = rpcErr?.status === 404 || rpcErr?.response?.status === 404;
        const isFunctionNotFound = (rpcErr?.message || rpcErr?.errorMessage || "").includes("Could not find the function");
        if (is404 || isFunctionNotFound) {
          const result = await postgrestClient.get("ano_letivo", {
            filters: effectiveFilters,
            order,
            limit,
            offset,
            includeCount: false,
          });
          return Array.isArray(result) ? result : result ? [result] : [];
        }
        throw rpcErr;
      }
    } else {
      // Para casos com filtros, usar consulta direta (sem estatísticas)
      const result = await postgrestClient.get("ano_letivo", {
        filters: effectiveFilters,
        order,
        limit,
        offset,
        includeCount,
      });

      // Se includeCount, retorna { data, total }, senão retorna array direto
      if (includeCount && result && typeof result === 'object' && 'data' in result) {
        return result;
      }
      return result;
    }
  },

  /**
   * Busca ano letivo por ID
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("ano_letivo", {
      filters: { ano_id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria novo ano letivo
   * @param {Object} anoData
   * @returns {Promise<Object>}
   */
  async create(anoData) {
    // Usa função RPC para evitar problemas de permissão na sequência
    // Multitenant: p_negocio_id opcional; quando informado (perfil /me), associa o ano ao tenant
    try {
      const result = await postgrestClient.rpc("criar_ano_letivo", {
        p_ano: anoData.ano,
        p_dt_inicio: anoData.dt_inicio || anoData.dataInicio || `${anoData.ano}-01-01`,
        p_dt_fim: anoData.dt_fim || anoData.dataFim || `${anoData.ano}-12-31`,
        p_min_avaliacoes: anoData.min_avaliacoes || anoData.minAvaliacoes || 2,
        p_escala_niveis: anoData.escala_niveis || anoData.escalaNiveis || "1,2,3,4",
        p_status: anoData.status || "planejamento",
        p_negocio_id: anoData.negocio_id || null,
      });
      // A função retorna uma tabela, então pode vir como array
      const data = Array.isArray(result) ? (result.length > 0 ? result[0] : result) : result;
      return data;
    } catch (err) {
      // Se a função RPC falhar, loga o erro mas não tenta fallback
      // pois o método direto também falhará com o mesmo erro de permissão
      console.error("Erro ao criar ano letivo via RPC:", err);
      throw err;
    }
  },

  /**
   * Atualiza ano letivo
   * @param {string} id
   * @param {Object} anoData
   * @returns {Promise<Object>}
   */
  async update(id, anoData) {
    const result = await postgrestClient.patch("ano_letivo", { ano_id: id }, anoData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Valida se é possível encerrar um ano letivo
   * @param {string|number} anoId
   * @returns {Promise<Object>}
   */
  async validarEncerrar(anoId) {
    const result = await postgrestClient.rpc("validar_encerrar_ano_letivo", {
      p_ano_id: parseInt(anoId),
    });
    // A função retorna JSONB, então pode vir como objeto direto ou array
    return Array.isArray(result) ? (result.length > 0 ? result[0] : result) : result;
  },

  /**
   * Encerra um ano letivo (encerra matrículas e finaliza vínculos)
   * @param {string|number} anoId
   * @param {string} dataEncerramento - Data no formato YYYY-MM-DD
   * @returns {Promise<Object>}
   */
  async encerrar(anoId, dataEncerramento = null) {
    const params = {
      p_ano_id: parseInt(anoId),
    };
    
    if (dataEncerramento) {
      params.p_data_encerramento = dataEncerramento;
    }
    
    const result = await postgrestClient.rpc("encerrar_ano_letivo", params);
    // A função retorna uma tabela, então pode vir como array
    return Array.isArray(result) ? (result.length > 0 ? result[0] : result) : result;
  },
};

export default anosLetivosService;

